import { proxy } from "valtio";

// Minimal contract every entry in the device-store arrays must satisfy:
// `Id` is the discriminator used by toggleById and by the UI selection
// state. Extra fields (designation, peripheralType, shopId, ...) come
// from the backend payload and stay accessible via the index signature
// without forcing every consumer to enumerate them up-front.
export type Peripheral = {
    Id: string;
    designation?: string;
    [k: string]: unknown;
};

type DeviceStoreType = {
    allPrinters: Peripheral[];
    printerSelected: Peripheral[];
    selectedPeripherals: Peripheral[];
    isPrinterModified: boolean;
    checkModificationEffect: boolean;
    printers: Peripheral[];
    isConfigurationPrinterClicked: boolean;
    monetics: Peripheral[];
    cashManagements: Peripheral[];
    allMonetics: Peripheral[];
    allCashManagements: Peripheral[];
    selectedRowData: Partial<Peripheral>;
    allMoneticsData: Peripheral[];
    isConfigurationCashManagementClicked: boolean;
    isConfigurationMoneticClicked: boolean;
    editedSelectedRows: Record<string, unknown>[];
};

export const deviceStore = proxy<DeviceStoreType>({
    allPrinters: [],
    printerSelected: [],
    selectedPeripherals: [],
    isPrinterModified: false,
    checkModificationEffect: false,
    printers: [],
    isConfigurationPrinterClicked: false,
    monetics: [],
    cashManagements: [],
    allMonetics: [],
    allCashManagements: [],
    selectedRowData: {},
    allMoneticsData: [],
    isConfigurationCashManagementClicked: false,
    isConfigurationMoneticClicked: false,
    editedSelectedRows: [],
});

// ---------------------------------------------------------------------------
// Generic toggle helper — replaces 3 duplicated toggle functions
// ---------------------------------------------------------------------------

function toggleById<T extends { Id: string }>(list: T[], item: T): T[] {
    const exists = list.some((el) => el.Id === item.Id);
    if (exists) {
        return list.filter((el) => el.Id !== item.Id);
    }
    return [...list, item];
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export function resetDeviceStore() {
    deviceStore.printers = [];
    deviceStore.monetics = [];
    deviceStore.cashManagements = [];
    deviceStore.printerSelected = [];
}

export function resetAllPeripherals() {
    deviceStore.allPrinters = [];
    deviceStore.allMonetics = [];
}

export function setAllPrinters(printer: Peripheral) {
    deviceStore.allPrinters.push(printer);
}

export function setInAllPrinters(printer: Peripheral) {
    deviceStore.printerSelected = toggleById(
        [...deviceStore.printerSelected],
        printer
    );
}

export function setIsPrinterModified(isPrinterModified: boolean) {
    deviceStore.isPrinterModified = isPrinterModified;
}

export function setCheckModificationEffect(x: boolean) {
    deviceStore.checkModificationEffect = x;
}

export function setIsConfigurationPrinterClicked(value: boolean) {
    deviceStore.isConfigurationPrinterClicked = value;
}

export function setSelectedPeripherals(selectedPeripheral: Peripheral) {
    deviceStore.selectedPeripherals = toggleById(
        [...deviceStore.selectedPeripherals],
        selectedPeripheral
    );
}

export function setMonetics(monetic: Peripheral) {
    deviceStore.monetics = toggleById([...deviceStore.monetics], monetic);
}

export function setSelectedRowData(
    cashManagementRowData: Partial<Peripheral>
) {
    deviceStore.selectedRowData = cashManagementRowData;
}

export function setAllMoneticsData(monetics: Peripheral) {
    deviceStore.allMoneticsData.push(monetics);
}

export function setCashManagements(cashManagement: Peripheral) {
    deviceStore.cashManagements = toggleById(
        [...deviceStore.cashManagements],
        cashManagement
    );
}

export function setIsConfigurationCashManagementClicked(value: boolean) {
    deviceStore.isConfigurationCashManagementClicked = value;
}

export function setIsConfigurationMoneticClicked(value: boolean) {
    deviceStore.isConfigurationMoneticClicked = value;
}

export function setEditedSelectedRows(rows: Record<string, unknown>[]) {
    deviceStore.editedSelectedRows = rows;
}

export default deviceStore;
